import { useState } from 'react';
import { ACCENT, ACCENT_SOFT } from './palette';

/** Box delivery timeline — six phases, shortest duration solid, slower case translucent. */

interface Phase {
  n: string;
  title: string;
  detail: string;
  start: number;
  min: number;
  max: number;
  ongoing?: boolean;
}

const phases: Phase[] = [
  { n: '01', title: 'Assessment', detail: 'Your workload replayed on the stack, sources, identity, sizing — and the acceptance thresholds Fnk is held to.', start: 0, min: 2, max: 3 },
  { n: '02', title: 'Hardware', detail: 'Delivered, racked and powered — or your own provisioning against the published specification.', start: 2, min: 4, max: 8 },
  { n: '03', title: 'Connectors', detail: 'Configured against your systems, with permissions mapped.', start: 6, min: 1, max: 2 },
  { n: '04', title: 'Knowledge', detail: 'Your sources ingested, grounding validated on real questions.', start: 7, min: 1, max: 2 },
  { n: '05', title: 'Acceptance', detail: 'Tested against the thresholds agreed in writing at step one.', start: 8, min: 1, max: 1 },
  { n: '06', title: 'Production', detail: 'Live, with support and updates running.', start: 9, min: 1, max: 1, ongoing: true },
];

const AXIS_MAX = 16;

export function DeploymentTimeline() {
  const [hovered, setHovered] = useState<string | null>(null);
  const pct = (weeks: number) => (weeks / AXIS_MAX) * 100;

  return (
    <figure className="m-0">
      <div className="relative mb-2 ml-0 h-5 sm:ml-[9.5rem]">
        {[0, 4, 8, 12, 16].map((w) => (
          <div key={w} className="absolute top-0 -translate-x-1/2" style={{ left: `${pct(w)}%` }}>
            <span className="font-mono text-[10px] tabular text-ink-4">{w === 0 ? '0' : `${w}w`}</span>
          </div>
        ))}
      </div>

      <div className="space-y-2.5">
        {phases.map((phase) => {
          const isHovered = hovered === phase.n;
          return (
            <div key={phase.n} className="cursor-default sm:flex sm:items-center sm:gap-4" onMouseEnter={() => setHovered(phase.n)} onMouseLeave={() => setHovered(null)} onFocus={() => setHovered(phase.n)} onBlur={() => setHovered(null)} tabIndex={0}>
              <div className="mb-1 flex items-baseline gap-2 sm:mb-0 sm:w-[8.5rem] sm:shrink-0">
                <span className="font-mono text-[11px] text-accent-ink">{phase.n}</span>
                <span className={`text-[13px] font-medium transition-colors ${isHovered ? 'text-ink' : 'text-ink-2'}`}>{phase.title}</span>
              </div>
              <div className="relative h-7 flex-1 rounded-sm bg-paper-2">
                {[4, 8, 12].map((w) => (
                  <div key={w} className="absolute inset-y-0 w-px bg-line" style={{ left: `${pct(w)}%` }} aria-hidden="true" />
                ))}
                {phase.max > phase.min && !phase.ongoing && (
                  <div className="absolute inset-y-1.5" style={{ left: `${pct(phase.start + phase.min)}%`, width: `${pct(phase.max - phase.min)}%`, background: ACCENT_SOFT }} />
                )}
                <div
                  className="absolute inset-y-1.5 transition-opacity"
                  style={{
                    left: `${pct(phase.start)}%`,
                    width: phase.ongoing ? `${pct(AXIS_MAX - phase.start)}%` : `${pct(phase.min)}%`,
                    background: phase.ongoing ? `linear-gradient(to right, ${ACCENT}, ${ACCENT_SOFT})` : ACCENT,
                    opacity: hovered && !isHovered ? 0.5 : 1,
                  }}
                />
                <span className="absolute inset-y-0 right-2 flex items-center font-mono text-[10.5px] tabular text-ink-3">
                  {phase.ongoing ? 'ongoing' : phase.min === phase.max ? `${phase.min}w` : `${phase.min}–${phase.max}w`}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 min-h-[3.25rem] border-t border-line pt-4">
        {hovered ? (
          <p className="text-[13px] leading-relaxed text-ink-2">
            <span className="font-medium text-ink">{phases.find((p) => p.n === hovered)?.title}:</span> {phases.find((p) => p.n === hovered)?.detail}
          </p>
        ) : (
          <p className="text-[13px] leading-relaxed text-ink-2">
            <span className="font-semibold text-ink">9 to 16 weeks</span> from signature to production. The partial box replaces step 02 with your own
            provisioning, which usually shortens it. Hover any phase for detail.
          </p>
        )}
      </div>
    </figure>
  );
}
